import type React from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import { bgnToEur } from '../../domain/currency';
import { formatGenerationName, type GenerationSummary } from '../../domain/pricing';

function formatEur(bgn: number): string {
  return `${Math.round(bgnToEur(bgn)).toLocaleString()} €`;
}

interface GenerationGuideProps {
  model: string;
  generations: GenerationSummary[];
  /** When given, rows toggle which generations the page (prices, chart and ads) is narrowed to. */
  selected?: string[];
  onToggle?: (generation: string) => void;
}

/** What each generation of the model typically costs, so a buyer can see which one fits their budget. */
export function GenerationGuide({ model, generations, selected = [], onToggle }: GenerationGuideProps) {
  if (generations.length === 0) {
    return null;
  }

  return (
    <Card sx={{ p: 3 }}>
      <Typography variant="h6">Price by generation</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Average asking price in last week&apos;s ads, cheapest first.
        {onToggle ? ' Tap one to only see its prices and ads.' : ''}
      </Typography>
      <Stack divider={<Divider flexItem />}>
        {generations.map((summary) => (
          <Stack
            key={summary.generation}
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            {...(onToggle && {
              role: 'button',
              tabIndex: 0,
              'aria-pressed': selected.includes(summary.generation),
              onClick: () => onToggle(summary.generation),
              onKeyDown: (event: React.KeyboardEvent) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  onToggle(summary.generation);
                }
              },
            })}
            sx={{
              py: 1.25,
              px: 1,
              mx: -1,
              borderRadius: 1,
              cursor: onToggle ? 'pointer' : 'default',
              bgcolor: selected.includes(summary.generation) ? 'action.selected' : 'transparent',
              '&:hover': onToggle ? { bgcolor: 'action.hover' } : undefined,
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography fontWeight={600}>{formatGenerationName(summary.generation, model)}</Typography>
              <Typography variant="caption" color="text.secondary">
                {summary.adCount.toLocaleString()} ads · {summary.engineCount} {summary.engineCount === 1 ? 'engine' : 'engines'}
              </Typography>
            </Box>
            <Box sx={{ textAlign: 'right', flexShrink: 0, pl: 2 }}>
              <Typography fontWeight={700}>{formatEur(summary.averagePrice)}</Typography>
              {summary.cheapest !== null && summary.priciest !== null && (
                <Typography variant="caption" color="text.secondary">
                  {formatEur(summary.cheapest)} - {formatEur(summary.priciest)}
                </Typography>
              )}
            </Box>
          </Stack>
        ))}
      </Stack>
    </Card>
  );
}
